/**
 * <TrashStatsCard /> — Admin Overview widget for soft-deleted lead health.
 *
 * GET /api/admin/trash/stats returns:
 *   total, due_in_24h, due_in_7d, oldest_days, by_stage{}, last_sweep{}
 *
 * Two-tone card with three stat tiles + a stage-breakdown chip row + a
 * "Last cron sweep" footer. Refreshes every 60s.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, AlertTriangle, Clock, RefreshCw, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

function fmtAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1)  return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function TrashStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/trash/stats");
      setData(data);
    } catch { setData(null); }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(false), 60_000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="card p-5" data-testid="admin-trash-stats-loading">
        <div className="text-xs text-[var(--ink-2)] flex items-center gap-1.5">
          <Trash2 size={11} /> Loading trash stats…
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card p-5" data-testid="admin-trash-stats-error">
        <div className="text-xs text-red-600 flex items-center gap-1.5">
          <AlertTriangle size={11} /> Trash stats unavailable
        </div>
      </div>
    );
  }

  const { total, due_in_24h: due24, due_in_7d: due7, oldest_days,
          by_stage, last_sweep } = data;
  const hasPressure = due24 > 0;
  const stageRows = Object.entries(by_stage || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div
      className="card p-5 border-2 border-slate-200"
      data-testid="admin-trash-stats"
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-rose-600 flex items-center gap-1.5">
            <Trash2 size={11} /> Trash health
          </div>
          <div className="font-display font-extrabold text-xl mt-0.5">
            {total} lead{total === 1 ? "" : "s"} in trash
          </div>
          {oldest_days != null && (
            <div className="text-xs text-[var(--ink-2)] mt-0.5">
              Oldest sitting for <b>{oldest_days} day{oldest_days === 1 ? "" : "s"}</b>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          className="p-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 transition"
          title="Refresh"
          data-testid="admin-trash-stats-refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
        </button>
      </header>

      {/* Three stat tiles */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatTile
          label="Purges next 24h"
          value={due24}
          warn={due24 > 0}
          testId="admin-trash-stats-due-24"
        />
        <StatTile
          label="Purges next 7d"
          value={due7}
          testId="admin-trash-stats-due-7d"
        />
        <StatTile
          label="Total trashed"
          value={total}
          testId="admin-trash-stats-total"
        />
      </div>

      {/* Stage breakdown chips */}
      {stageRows.length > 0 && (
        <div className="mb-4" data-testid="admin-trash-stats-stage-breakdown">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--ink-2)] mb-2">
            By stage
          </div>
          <div className="flex flex-wrap gap-1.5">
            {stageRows.map(([stage, n]) => (
              <span
                key={stage}
                className="text-[11px] font-semibold px-2 py-1 rounded-full bg-stone-100 text-stone-700"
              >
                {stage} <span className="text-stone-500 ml-1">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Last sweep footer */}
      <div
        className="flex items-center justify-between gap-2 pt-3 border-t border-stone-200"
        data-testid="admin-trash-stats-last-sweep"
      >
        <div className="text-[11px] text-[var(--ink-2)] flex items-center gap-1.5">
          <Clock size={11} className="text-[var(--ink-3)]" />
          {last_sweep ? (
            <span>
              Last sweep <b>{fmtAgo(last_sweep.ran_at)}</b> · purged{" "}
              <b>{last_sweep.swept || 0}</b> lead{last_sweep.swept === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="italic">No sweep recorded yet</span>
          )}
        </div>
        <Link
          to="/app/leads/trash"
          className="text-[11px] font-bold text-rose-700 hover:text-rose-900 flex items-center gap-1"
          data-testid="admin-trash-stats-open-trash"
        >
          Open trash <ArrowRight size={11} />
        </Link>
      </div>

      {hasPressure && (
        <div
          className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-[11px] text-amber-900 flex items-start gap-2"
          data-testid="admin-trash-stats-pressure-banner"
        >
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            <b>{due24}</b> lead{due24 === 1 ? " is" : "s are"} crossing the
            30-day window in the next 24h. The next sweep will hard-purge
            them and their dependents (recordings, snapshots, SMS).
          </span>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, warn = false, testId }) {
  return (
    <div
      className={
        "p-3 rounded-lg border-2 " +
        (warn ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-stone-50")
      }
      data-testid={testId}
    >
      <div className={"text-2xl font-display font-extrabold " + (warn ? "text-amber-900" : "text-stone-900")}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide font-bold text-[var(--ink-2)] mt-0.5">
        {label}
      </div>
    </div>
  );
}
